import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralIssues = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [issues, setIssues] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    title: "",
    description: "",
    assignedTo: "",
    priority: "Medium",
    severity: "Minor",
    status: "Open",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // Sample Data
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Portal" },
  ];

  const demoIssues = [
    {
      id: 1,
      projectId: 1,
      title: "Login button not working",
      description: "Clicking login does nothing",
      assignedTo: "Rahul",
      priority: "High",
      severity: "Major",
      status: "Open",
    },
    {
      id: 2,
      projectId: 1,
      title: "UI alignment broken",
      description: "Header misaligned in mobile",
      assignedTo: "Priya",
      priority: "Medium",
      severity: "Minor",
      status: "Open",
    },
    {
      id: 3,
      projectId: 2,
      title: "Stock sync delay",
      description: "ERP stock sync lagging",
      assignedTo: "Amit",
      priority: "High",
      severity: "Critical",
      status: "Investigating",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setIssues(demoIssues);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAdd = () => {
    if (!selectedProjectId) {
      alert("Please select project first.");
      return;
    }

    setEditingIssue(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (issue) => {

    setEditingIssue(issue);

    setFormData({
      title: issue.title,
      description: issue.description,
      assignedTo: issue.assignedTo,
      priority: issue.priority,
      severity: issue.severity,
      status: issue.status,
    });

    setShowModal(true);

  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this issue?")) return;
    setIssues((prev) => prev.filter((i) => i.id !== id));
  };

  const handleSubmit = (e) => {

    e.preventDefault();

    const newIssue = {
      id: editingIssue?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingIssue) {
      setIssues((prev) =>
        prev.map((i) => (i.id === editingIssue.id ? newIssue : i))
      );
    } else {
      setIssues((prev) => [...prev, newIssue]);
    }

    setShowModal(false);
    setFormData(initialForm);

  };

  // -----------------------
  // Filters
  // -----------------------

  const projectIssues = issues.filter(
    (i) => String(i.projectId) === String(selectedProjectId)
  );

  const filtered = projectIssues.filter(
    (i) =>
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      (i.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const statusColor = {
    Open: "bg-red-600",
    Investigating: "bg-yellow-600",
    Fixed: "bg-green-600",
    Closed: "bg-gray-600",
  };

  return (

    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">

      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">

        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            ❗ SprintCentral Issues
          </div>
          <div className="text-xs text-gray-400">
            Track Bugs & Project Issues
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

      </header>

      <div className="mt-4 min-h-[75vh]">

        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">

          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Issues
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Issue
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">

            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search issues..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />

          </div>

        </div>

        {/* MODAL */}

        {showModal && (

          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">

            <div className="flex justify-between items-center mb-4">

              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingIssue ? "Edit Issue" : "New Issue"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

            </div>

            <form onSubmit={handleSubmit} className="space-y-3">

              <input
                type="text"
                placeholder="Issue Title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Issue Description"
                style={{ height: "100px", width: "100%" }}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <input
                type="text"
                placeholder="Assigned To"
                value={formData.assignedTo}
                onChange={(e) =>
                  setFormData({ ...formData, assignedTo: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <div className="flex gap-3">

                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-32 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>

                <select
                  value={formData.severity}
                  onChange={(e) =>
                    setFormData({ ...formData, severity: e.target.value })
                  }
                  className="w-32 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option>Critical</option>
                  <option>Major</option>
                  <option>Minor</option>
                </select>

                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option>Open</option>
                  <option>Investigating</option>
                  <option>Fixed</option>
                  <option>Closed</option>
                </select>

              </div>

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingIssue ? "Update Issue" : "Create Issue"}
              </button>

            </form>

          </div>

        )}

        {/* ISSUE CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">

          {paginated.map((issue) => (

            <div
              key={issue.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >

              <div className="flex justify-between items-start">

                <h3 className="font-semibold text-white">{issue.title}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[issue.status]}`}
                >
                  {issue.status}
                </span>

              </div>

              <p className="text-sm text-gray-300 mt-2">
                {issue.description || "No description"}
              </p>

              <div className="text-xs text-gray-400 mt-3">
                Assigned: {issue.assignedTo || "-"}
              </div>

              <div className="text-xs text-gray-400">
                Priority: {issue.priority} | Severity: {issue.severity}
              </div>

              <div className="mt-4 flex gap-2">

                <button
                  onClick={() => handleEdit(issue)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(issue.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>

              </div>

            </div>

          ))}

        </div>

        {/* PAGINATION */}

        {totalPages > 1 && (

          <div className="flex justify-center items-center gap-4 mt-10">

            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

          </div>

        )}

      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4">

        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">

          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">

            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>

            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>

            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>

            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>

          </div>

        </div>

      </footer>

    </div>

  );

};

export default SprintCentralIssues;